/**
 * CONTACT PAGE - Redirects to main website
 * The contact/booking is centralized on drdnreddyortho.com
 */

import React, { useEffect } from 'react';
import { useSite } from '../contexts/SiteContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { 
  MapPin, 
  Phone, 
  Mail, 
  Clock,
  ExternalLink,
  ArrowRight
} from 'lucide-react';

export const Contact = () => {
  const { theme, doctor, bodyPart, specialtyLabel, isMainSite } = useSite();

  // If this is a specialty site, show redirect to main site
  const mainSiteUrl = 'https://drdnreddyortho.com';

  return (
    <div className="min-h-screen" data-testid="contact-page">
      {/* Hero Section */}
      <section className="py-20 text-white" style={{ backgroundColor: theme.primaryHex }}>
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Consult {doctor.name}
            </h1>
            <p className="text-xl opacity-90 leading-relaxed">
              Get expert evaluation and personalized treatment for your {bodyPart.toLowerCase()} condition
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            
            {/* Redirect Card */}
            <Card className="mb-12 border-2" style={{ borderColor: theme.primaryHex }}>
              <CardHeader className="text-center">
                <CardTitle className="text-2xl">Book Your Appointment</CardTitle>
                <CardDescription className="text-base">
                  For appointments and detailed information, visit our main website
                </CardDescription>
              </CardHeader>
              <CardContent className="text-center">
                <a href={`${mainSiteUrl}/about`}>
                  <Button 
                    size="lg" 
                    className="text-white"
                    style={{ backgroundColor: theme.primaryHex }}
                  >
                    Visit Dr. Reddy&apos;s Profile
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </a>
                <p className="text-sm text-gray-500 mt-4">
                  You&apos;ll be taken to drdnreddyortho.com
                </p>
              </CardContent>
            </Card>

            {/* Quick Contact Info */}
            <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Quick Contact</h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <Card style={{ borderLeftColor: theme.primaryHex, borderLeftWidth: '4px' }}>
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-3">
                    <Phone className="w-6 h-6" style={{ color: theme.primaryHex }} />
                    <CardTitle>Call Now</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <a 
                    href={`tel:${doctor.phone}`} 
                    className="text-xl font-bold hover:opacity-80 transition-colors"
                    style={{ color: theme.primaryHex }}
                  >
                    {doctor.phone}
                  </a>
                  <p className="text-sm text-gray-500 mt-1">Tap to call directly</p>
                </CardContent>
              </Card>

              <Card style={{ borderLeftColor: theme.primaryHex, borderLeftWidth: '4px' }}>
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-3">
                    <svg className="w-6 h-6" style={{ color: theme.primaryHex }} viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                    </svg>
                    <CardTitle>WhatsApp</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <a 
                    href={doctor.whatsappLink} 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-lg font-medium hover:opacity-80 transition-colors"
                    style={{ color: theme.primaryHex }}
                  >
                    Message on WhatsApp
                  </a>
                  <p className="text-sm text-gray-500 mt-1">Quick response during clinic hours</p>
                </CardContent>
              </Card>

              <Card style={{ borderLeftColor: theme.primaryHex, borderLeftWidth: '4px' }}>
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-3">
                    <MapPin className="w-6 h-6" style={{ color: theme.primaryHex }} />
                    <CardTitle>Location</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 font-medium">{doctor.hospitalFull}</p>
                  <p className="text-gray-600 text-sm mt-1">{doctor.addressShort}</p>
                  <a 
                    href={doctor.googleMapsLink} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 mt-2 text-sm font-medium"
                    style={{ color: theme.primaryHex }}
                  >
                    Get Directions <ExternalLink className="w-3 h-3" />
                  </a>
                </CardContent>
              </Card>

              <Card style={{ borderLeftColor: theme.primaryHex, borderLeftWidth: '4px' }}>
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-3">
                    <Clock className="w-6 h-6" style={{ color: theme.primaryHex }} />
                    <CardTitle>Clinic Hours</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 font-medium">{doctor.timings}</p>
                  <p className="text-gray-500 text-sm mt-1">By Appointment Only</p>
                </CardContent>
              </Card>
            </div>

            {/* Main Site Link */}
            <div className="text-center mt-12 p-8 rounded-xl" style={{ backgroundColor: theme.lightBgHex }}>
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Want to learn more about Dr. Reddy?
              </h3>
              <p className="text-gray-600 mb-6">
                Visit the main website for detailed profile, patient testimonials, and online booking
              </p>
              <a href={mainSiteUrl}>
                <Button 
                  variant="outline"
                  size="lg"
                  style={{ borderColor: theme.primaryHex, color: theme.primaryHex }}
                >
                  Visit drdnreddyortho.com
                  <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              </a>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
};
